import { request } from "$/lib/api"
import {
  Route,
  ServiceAction,
  StartWorkTimeRecording,
  UpdateWorkTimeRecording,
  WorkTimeRecording,
  WorkTimeRecordingDataPoint,
  WorkTimeRecordingRow,
  WorkTimeRecordingStats,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

async function fetchByJob(jobId: string): Promise<WorkTimeRecording[]> {
  return await request<WorkTimeRecording[]>({
    url: populateRoute(Route.WORK_TIME_RECORDING__FIND_BY_JOB, jobId),
    method: "GET",
  })
}

async function updateRecording(
  jobId: string,
  recordingId: string,
  data: UpdateWorkTimeRecording,
) {
  return await request<void>({
    url: populateRoute(Route.WORK_TIME_RECORDING__UPDATE, jobId, recordingId),
    method: "PATCH",
    data,
  })
}

async function deleteRecording(jobId: string, recordingId: string) {
  return await request<void>({
    url: populateRoute(Route.WORK_TIME_RECORDING__DELETE, jobId, recordingId),
    method: "DELETE",
  })
}

async function fetchDataPointsByPartAction(
  storeId: string,
  partId: string,
  action: ServiceAction,
): Promise<WorkTimeRecordingDataPoint[]> {
  return await request<WorkTimeRecordingDataPoint[]>({
    url: populateRoute(
      Route.WORK_TIME_RECORDING__FIND_MANY_BY_PART_ACTION,
      storeId,
      partId,
      action,
    ),
    method: "GET",
  })
}

async function fetchStats(storeId: string): Promise<WorkTimeRecordingStats> {
  return await request<WorkTimeRecordingStats>({
    url: populateRoute(Route.WORK_TIME_RECORDING__STATS_BY_STORE, storeId),
    method: "GET",
  })
}

async function startRecording(jobId: string, start: StartWorkTimeRecording) {
  return await request<WorkTimeRecording>({
    url: populateRoute(Route.WORK_TIME_RECORDING__START, jobId),
    method: "POST",
    data: start,
  })
}

async function stopRecording(jobId: string, row: WorkTimeRecordingRow) {
  return await request<void>({
    url: populateRoute(Route.WORK_TIME_RECORDING__STOP, jobId),
    method: "POST",
    data: row,
  })
}

const workTimeRecordingQueries = {
  all: () => ["work-time-recording"],
  byJob: (jobId: string) =>
    queryOptions({
      queryKey: [...workTimeRecordingQueries.all(), "job", jobId],
      queryFn: () => fetchByJob(jobId),
    }),
  dataPointsByPartAction: (
    storeId: string,
    partId: string,
    action: ServiceAction,
  ) =>
    queryOptions({
      queryKey: [
        ...workTimeRecordingQueries.all(),
        "data-points",
        storeId,
        partId,
        action,
      ],
      queryFn: () => fetchDataPointsByPartAction(storeId, partId, action),
    }),
  stats: (storeId: string) =>
    queryOptions({
      queryKey: [...workTimeRecordingQueries.all(), "stats", storeId],
      queryFn: () => fetchStats(storeId),
    }),
}

export const useWorkTimeRecordingMutations = (jobId: string) => {
  const queryClient = useQueryClient()

  const invalidateQueries = async () =>
    await queryClient.invalidateQueries({
      queryKey: workTimeRecordingQueries.all(),
    })

  const startMutation = useMutation({
    mutationFn: (start: StartWorkTimeRecording) => startRecording(jobId, start),
    onSettled: invalidateQueries,
  })

  const stopMutation = useMutation({
    mutationFn: (row: WorkTimeRecordingRow) => stopRecording(jobId, row),
    onSettled: invalidateQueries,
  })

  const updateMutation = useMutation({
    mutationFn: ({
      recordingId,
      data,
    }: { recordingId: string; data: UpdateWorkTimeRecording }) =>
      updateRecording(jobId, recordingId, data),
    onSettled: invalidateQueries,
  })

  const deleteMutation = useMutation({
    mutationFn: (recordingId: string) => deleteRecording(jobId, recordingId),
    onSettled: invalidateQueries,
  })

  return { startMutation, stopMutation, updateMutation, deleteMutation }
}

export default workTimeRecordingQueries
